// src/pages/qbo/ConversionTab.tsx
//
// Open-receivables conversion (3.17.0). For a company whose QuickBooks history
// is daily summary journals, the documents still OPEN at the cutoff are
// brought over as detailed invoices / credit memos at their open balance, and
// one offset journal per day cancels the income they re-book against
// Accounts Receivable under the legacy placeholder customer. Income, COGS and
// total AR for every period stay unchanged; AR moves from the placeholder
// lump to real customers.
//
// Everything here is read-only until "Convert selected" is confirmed, and the
// selection can be done in phases (by day or by document).

import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, XCircle } from 'lucide-react';
import DateField from '../../components/DateField';
import qboApi from '../../services/qboApi';
import SyncResultList from './SyncResultList';
import SnapshotsCard from './SnapshotsCard';
import CustomerCheckCard from './CustomerCheckCard';
import LumpsCard from './LumpsCard';
import CleanupCard from './CleanupCard';
import UnconvertCard from './UnconvertCard';
import type { AccountPick, ConversionConfig, ConversionReview, SyncResult } from './qboTypes';
import { money } from './qboTypes';
import { runJob, type JobView } from './qboJobs';

interface Props {
  onMessage: (type: 'error' | 'success' | 'info', text: string) => void;
  onRan?: () => void;
}

const fmtDate = (s?: string | null) => (s ? new Date(`${s}T00:00:00`).toLocaleDateString() : '');
/** The agent marks documents it will refuse (overpaid invoice, over-applied credit) with a "cannot convert" warning. */
const blockedRow = (r: { warnings: string[] }) => r.warnings.some((w) => w.startsWith('cannot convert'));

function Acct({ label, a }: { label: string; a: AccountPick }) {
  return (
    <div className="flex items-center justify-between gap-2 text-xs py-0.5">
      <span className="text-slate-500">{label}</span>
      <span className={`flex items-center gap-1 ${a.found ? 'text-slate-200' : 'text-red-300'}`} title={`configured: ${a.configured || '(default)'}${a.qboId ? ` · QBO id ${a.qboId}` : ''}`}>
        {a.found ? <CheckCircle2 size={12} className="text-green-400" /> : <XCircle size={12} />}
        {a.found ? a.resolved : `not found (${a.configured || 'default'})`}
      </span>
    </div>
  );
}

export default function ConversionTab({ onMessage, onRan }: Props) {
  const [config, setConfig] = useState<ConversionConfig | null>(null);
  const [cutoff, setCutoff] = useState('');
  const [since, setSince] = useState('');
  const [review, setReview] = useState<ConversionReview | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [job, setJob] = useState<JobView | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState<SyncResult | null>(null);
  const [offsetMode, setOffsetMode] = useState<'day' | 'fixed'>('day');
  const [offsetDate, setOffsetDate] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const c = (await qboApi.conversionConfig()) as ConversionConfig;
        setConfig(c);
        if (c.cutoff) setCutoff(c.cutoff);
        if (c.bookCloseDate) {
          const d = new Date(`${c.bookCloseDate}T00:00:00`);
          d.setDate(d.getDate() + 1);
          setOffsetDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
        }
      } catch (e: unknown) {
        onMessage('error', `Conversion config: ${(e as Error).message}`);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rowsByDay = useMemo(() => {
    const m = new Map<string, ConversionReview['rows']>();
    review?.rows.forEach((r) => { const l = m.get(r.date) || []; l.push(r); m.set(r.date, l); });
    return m;
  }, [review]);

  const selectedRows = useMemo(() => (review ? review.rows.filter((r) => selected.has(r.key)) : []), [review, selected]);
  const selectedOpen = selectedRows.reduce((s, r) => s + (r.balance || 0), 0);

  const load = async () => {
    if (!cutoff) { onMessage('error', 'Pick the cutoff date (last day covered by the summary journals).'); return; }
    setLoading(true);
    setResult(null);
    setConfirming(false);
    try {
      const r = (await qboApi.conversionReview(cutoff, since || undefined)) as ConversionReview;
      setReview(r);
      // Nothing selected by default: tick the days (or documents) you mean to convert.
      // The first live run on Devash almost converted every open document because the
      // review used to pre-select all of them.
      setSelected(new Set());
      setOpen(new Set());
    } catch (e: unknown) {
      onMessage('error', (e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const toggleRow = (k: string) => setSelected((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const dayState = (date: string): 'all' | 'some' | 'none' => {
    const rows = rowsByDay.get(date) || [];
    const n = rows.filter((r) => selected.has(r.key)).length;
    return n === 0 ? 'none' : n === rows.length ? 'all' : 'some';
  };
  const toggleDay = (date: string) => {
    const rows = rowsByDay.get(date) || [];
    const st = dayState(date);
    // A day with converted documents that no offset covers yet (interrupted run) is
    // ticked WITH its converted rows: the agent skips them and posts the catch-up offset.
    const uncovered = (review?.days.find((d) => d.date === date)?.uncovered ?? 0) > 0;
    setSelected((s) => {
      const n = new Set(s);
      rows.forEach((r) => { if (st === 'all') n.delete(r.key); else if (uncovered || ((!r.alreadySynced || r.modified) && !blockedRow(r))) n.add(r.key); });
      return n;
    });
  };
  const toggleOpen = (date: string) => setOpen((s) => { const n = new Set(s); if (n.has(date)) n.delete(date); else n.add(date); return n; });
  const selectAll = (what: 'new' | 'none') => setSelected(new Set(what === 'none' || !review ? [] : review.rows.filter((r) => (!r.alreadySynced || r.modified) && !blockedRow(r)).map((r) => r.key)));

  const run = async () => {
    if (selected.size === 0) return;
    if (!confirming) { setConfirming(true); setTimeout(() => setConfirming(false), 8000); return; }
    setConfirming(false);
    setRunning(true);
    setResult(null);
    setJob(null);
    try {
      const keys = Array.from(selected);
      const fixed = offsetMode === 'fixed' ? offsetDate : undefined;
      const r = await runJob(() => qboApi.conversionRun(keys, cutoff, fixed), setJob);
      setResult(r);
      onMessage(r.errors ? 'error' : 'success', `Conversion: ${r.created} documents posted, ${r.skipped} skipped, ${r.errors} errors`);
      onRan?.();
      const again = (await qboApi.conversionReview(cutoff, since || undefined)) as ConversionReview;
      setReview(again);
      const failed = new Set((r.details || []).filter((d) => d.status === 'error' && d.invoice).map((d) => String(d.invoice)));
      setSelected(new Set(again.rows.filter((x) => failed.has(x.key)).map((x) => x.key)));
    } catch (e: unknown) {
      onMessage('error', (e as Error).message);
    } finally {
      setRunning(false);
      setJob(null);
    }
  };

  const blocked = !!(review && !review.placeholderFound);

  return (
    <div>
      {/* What this does */}
      <div className="bg-slate-800 rounded-xl p-5 border border-slate-700 mb-5 text-sm text-slate-300">
        <div className="text-sm font-semibold text-slate-400 mb-2">Open receivables conversion</div>
        <p className="text-slate-400 text-[13px] leading-relaxed">
          QuickBooks history up to the cutoff is daily summary journals, with receivables held under one placeholder customer.
          This brings over only the invoices and credits still <em>open</em> at the cutoff, each at its open balance, to the real customer,
          and posts one offset journal per day (debit income, credit Accounts Receivable under the placeholder) so income, cost and total
          receivables for every period stay exactly as they are. Only the composition of receivables changes. Paid history is never resent.
          Payments received after the cutoff can then be applied to these documents.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
        {/* Setup */}
        <div className="bg-slate-800 rounded-xl p-5 border border-slate-700">
          <div className="text-sm font-semibold text-slate-400 mb-3">Cutoff</div>
          <div className="flex flex-wrap items-end gap-3">
            <DateField label="Last journal day" value={cutoff} onChange={setCutoff} />
            <DateField label="Only docs since (optional)" value={since} onChange={setSince} max={cutoff || undefined} />
            <button
              className="px-4 py-2 rounded-lg text-sm font-medium border border-slate-600 text-slate-300 hover:bg-slate-700 transition-colors disabled:opacity-50"
              onClick={load}
              disabled={loading || !cutoff}
            >
              {loading ? 'Loading…' : review ? 'Reload' : 'Load open documents'}
            </button>
          </div>
          {config && (
            <div className="mt-3 text-xs text-slate-400 space-y-1">
              <div>Configured cutoff: <span className="font-mono text-slate-200">{config.cutoff || '— (set Qbo:ConversionCutoff)'}</span></div>
              <div>
                Books closed through:{' '}
                {config.bookCloseDate
                  ? <span className="font-mono text-amber-300">{config.bookCloseDate}</span>
                  : <span className="text-green-400">not closed — original dates can be used</span>}
              </div>
              {config.cutoffExceptions && config.cutoffExceptions.length > 0 && (
                <div>No legacy journal on: <span className="font-mono text-amber-300">{config.cutoffExceptions.join(', ')}</span> <span className="text-slate-500">— pushed in full from Invoices{config.cutoffExceptionReceipts === false ? '; receipts treated as legacy' : ' and Payments'}, not converted</span></div>
              )}
              {config.cutoffExceptionsInvalid && config.cutoffExceptionsInvalid.length > 0 && (
                <div className="text-red-300">Qbo:CutoffExceptions has entries the agent cannot read as yyyy-MM-dd: <span className="font-mono">{config.cutoffExceptionsInvalid.join(', ')}</span> — those days will convert as legacy days.</div>
              )}
              <div>Payment date sent to QuickBooks: <span className="font-mono text-slate-200">Cash.{config.paymentDateField}</span></div>
              <div>Per-item COGS journals after the cutoff: <span className={config.postCogsJournals ? 'text-green-400' : 'text-slate-300'}>{config.postCogsJournals ? 'on' : 'off'}</span> · Items on invoice lines: <span className={config.useItems ? 'text-green-400' : 'text-slate-300'}>{config.useItems ? 'on' : 'off'}</span></div>
            </div>
          )}
        </div>

        {/* Accounts */}
        <div className="bg-slate-800 rounded-xl p-5 border border-slate-700">
          <div className="text-sm font-semibold text-slate-400 mb-2">QuickBooks accounts (resolved by name)</div>
          {config ? (
            <>
              <div className="flex items-center justify-between gap-2 text-xs py-0.5">
                <span className="text-slate-500">AR placeholder customer</span>
                <span className={`flex items-center gap-1 ${config.arPlaceholderCustomer.found ? 'text-slate-200' : 'text-red-300'}`}>
                  {config.arPlaceholderCustomer.found ? <CheckCircle2 size={12} className="text-green-400" /> : <XCircle size={12} />}
                  {config.arPlaceholderCustomer.name}{config.arPlaceholderCustomer.found ? '' : ' — not found'}
                </span>
              </div>
              <Acct label="Accounts Receivable" a={config.accounts.accountsReceivable} />
              <Acct label="Sales (items)" a={config.accounts.sales} />
              <Acct label="Freight" a={config.accounts.freight} />
              <Acct label="Discounts" a={config.accounts.discount} />
              <Acct label="COGS" a={config.accounts.cogs} />
              <Acct label="Inventory Asset" a={config.accounts.inventoryAsset} />
              <button
                className="mt-2 text-[11px] text-blue-300 hover:underline"
                onClick={async () => { try { await qboApi.resetAccounts(); setConfig((await qboApi.conversionConfig()) as ConversionConfig); onMessage('info', 'Account lookups refreshed'); } catch (e: unknown) { onMessage('error', (e as Error).message); } }}
              >
                re-resolve accounts
              </button>
            </>
          ) : <div className="text-xs text-slate-500">Loading…</div>}
        </div>
      </div>

      {config && config.cutoffExceptions && config.cutoffExceptions.length > 0 && (
        <UnconvertCard days={config.cutoffExceptions} onMessage={onMessage} />
      )}
      <SnapshotsCard onMessage={onMessage} />
      <CustomerCheckCard onMessage={onMessage} />
      <LumpsCard onMessage={onMessage} />
      <CleanupCard onMessage={onMessage} />

      {review && (
        <div className="bg-slate-800 rounded-xl p-5 border border-slate-700 mb-5">
          {/* Totals */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-3">
            <div className="bg-slate-900 rounded-lg p-3"><div className="text-[10px] uppercase text-slate-500">Open documents</div><div className="text-lg font-semibold text-white">{review.count}</div><div className="text-[11px] text-slate-500">{review.converted} converted</div></div>
            <div className="bg-slate-900 rounded-lg p-3"><div className="text-[10px] uppercase text-slate-500">Open invoices</div><div className="text-lg font-semibold text-white font-mono">${money(review.openInvoices)}</div></div>
            <div className="bg-slate-900 rounded-lg p-3"><div className="text-[10px] uppercase text-slate-500">Open credits</div><div className="text-lg font-semibold text-amber-300 font-mono">${money(review.openCredits)}</div></div>
            <div className="bg-slate-900 rounded-lg p-3"><div className="text-[10px] uppercase text-slate-500">Net open at cutoff</div><div className="text-lg font-semibold text-white font-mono">${money(review.openTotal)}</div><div className="text-[11px] text-slate-500">compare to placeholder AR</div></div>
            <div className="bg-slate-900 rounded-lg p-3"><div className="text-[10px] uppercase text-slate-500">Days</div><div className="text-lg font-semibold text-white">{review.days.length}</div><div className="text-[11px] text-slate-500">one offset per day</div></div>
          </div>

          <ul className="mb-3 text-[11px] text-slate-400 space-y-0.5">
            {review.notes.map((n, i) => <li key={i} className={n.includes('NOT found') ? 'text-red-300' : ''}>• {n}</li>)}
          </ul>

          {/* Offset dating */}
          <div className="flex flex-wrap items-center gap-4 mb-3 text-xs text-slate-300">
            <span className="text-slate-500">Offset journals dated:</span>
            <label className="flex items-center gap-1 cursor-pointer"><input type="radio" checked={offsetMode === 'day'} onChange={() => setOffsetMode('day')} /> on each document's day (keeps every period unchanged)</label>
            <label className="flex items-center gap-1 cursor-pointer"><input type="radio" checked={offsetMode === 'fixed'} onChange={() => setOffsetMode('fixed')} /> all on one date</label>
            {offsetMode === 'fixed' && <DateField value={offsetDate} onChange={setOffsetDate} />}
            {review.bookCloseDate && offsetMode === 'day' && (
              <span className="text-amber-300 flex items-center gap-1"><AlertTriangle size={12} /> books closed through {review.bookCloseDate}: days on/before it will be rejected</span>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2 mb-2 text-xs">
            <button className="text-blue-300 hover:underline" onClick={() => selectAll('new')}>select all not converted{review.rows.some((r) => r.modified) ? ' + stale stubs' : ''}</button>
            <button className="text-blue-300 hover:underline" onClick={() => selectAll('none')}>clear</button>
            <button className="text-blue-300 hover:underline" onClick={() => setOpen(new Set(review.days.map((d) => d.date)))}>expand all</button>
            <button className="text-blue-300 hover:underline" onClick={() => setOpen(new Set())}>collapse all</button>
          </div>

          {/* Days */}
          <div className="overflow-x-auto max-h-[560px] overflow-y-auto rounded-lg border border-slate-700/60">
            <table className="w-full text-sm border-collapse">
              <thead className="sticky top-0 bg-slate-800">
                <tr>
                  <th className="px-2 py-2 border-b border-slate-700 w-8" />
                  <th className="px-2 py-2 border-b border-slate-700 w-6" />
                  <th className="text-left px-3 py-2 border-b border-slate-700 text-slate-500 font-semibold text-[11px] uppercase">Day</th>
                  <th className="text-right px-3 py-2 border-b border-slate-700 text-slate-500 font-semibold text-[11px] uppercase">Docs</th>
                  <th className="text-right px-3 py-2 border-b border-slate-700 text-slate-500 font-semibold text-[11px] uppercase">Invoices / credits</th>
                  <th className="text-right px-3 py-2 border-b border-slate-700 text-slate-500 font-semibold text-[11px] uppercase">Open at cutoff</th>
                  <th className="text-left px-3 py-2 border-b border-slate-700 text-slate-500 font-semibold text-[11px] uppercase">Offset journals</th>
                </tr>
              </thead>
              <tbody>
                {review.days.map((d) => {
                  const st = dayState(d.date);
                  const rows = rowsByDay.get(d.date) || [];
                  const expanded = open.has(d.date);
                  const closed = !!review.bookCloseDate && d.date <= review.bookCloseDate;
                  return (
                    <React.Fragment key={d.date}>
                      <tr className={`hover:bg-slate-700/30 ${st !== 'none' ? 'bg-blue-950/30' : ''}`}>
                        <td className="px-2 py-1.5 border-b border-slate-800/60">
                          <input type="checkbox" checked={st === 'all'} ref={(el) => { if (el) el.indeterminate = st === 'some'; }} onChange={() => toggleDay(d.date)} disabled={d.converted === d.count && !d.uncovered && !d.stale} />
                        </td>
                        <td className="px-2 py-1.5 border-b border-slate-800/60 text-slate-400 cursor-pointer" onClick={() => toggleOpen(d.date)}>{expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</td>
                        <td className="px-3 py-1.5 border-b border-slate-800/60 text-slate-200 whitespace-nowrap cursor-pointer" onClick={() => toggleOpen(d.date)}>
                          {fmtDate(d.date)} <span className="text-slate-500 font-mono text-[11px]">{d.date}</span>
                          {closed && <span className="ml-2 text-[10px] text-amber-300">closed period</span>}
                        </td>
                        <td className="px-3 py-1.5 border-b border-slate-800/60 text-right text-slate-300">
                          {d.count}{d.converted ? <span className="text-green-400 text-[11px]"> ({d.converted} done)</span> : null}
                          {d.uncovered ? <div className="text-amber-300 text-[10px] whitespace-nowrap" title="Converted by a run that was interrupted before its offset journal. Tick the day and run to post the catch-up offset.">{d.uncovered} not covered by an offset</div> : null}
                          {d.stale ? <div className="text-amber-300 text-[10px] whitespace-nowrap" title="Converted stubs whose open balance at the cutoff changed since (a receipt on a day that became a cutoff exception). Tick them and run: the stub is rewritten and the difference booked in a new offset for this day (agent 3.19.7).">{d.stale} stale stub{d.stale > 1 ? 's' : ''} ({d.staleDelta && d.staleDelta > 0 ? '+' : ''}{money(d.staleDelta ?? 0)})</div> : null}
                        </td>
                        <td className="px-3 py-1.5 border-b border-slate-800/60 text-right text-slate-400 text-xs">{d.invoices} / {d.credits}</td>
                        <td className="px-3 py-1.5 border-b border-slate-800/60 text-right font-mono text-xs text-slate-200">${money(d.openTotal)}</td>
                        <td className="px-3 py-1.5 border-b border-slate-800/60 text-xs">
                          {d.offsets.length === 0 ? <span className="text-slate-600">—</span> : d.offsets.map((o) => (
                            <span key={o.key} className={`inline-block mr-1 px-1.5 py-0.5 rounded ${o.status === 'Success' ? 'bg-green-900/60 text-green-300' : 'bg-red-900/60 text-red-300'}`} title={o.error || `QBO ${o.qboId}`}>{o.key.split('#')[1] ? `#${o.key.split('#')[1]}` : o.key}{o.qboId ? ` → ${o.qboId}` : ''}</span>
                          ))}
                        </td>
                      </tr>
                      {expanded && rows.map((r) => (
                        <tr key={r.key} className="bg-slate-900/50 hover:bg-slate-700/20">
                          <td className="px-2 py-1 border-b border-slate-800/40"><input type="checkbox" checked={selected.has(r.key)} onChange={() => toggleRow(r.key)} disabled={(r.alreadySynced && r.syncAction?.startsWith('Convert') && !r.modified) || blockedRow(r)} /></td>
                          <td className="border-b border-slate-800/40" />
                          <td className="px-3 py-1 border-b border-slate-800/40 text-xs" colSpan={2}>
                            <span className="font-mono text-slate-300">{r.key}</span>
                            <span className={`ml-2 ${r.type === 'CreditMemo' ? 'text-amber-300' : 'text-slate-500'}`}>{r.type === 'CreditMemo' ? 'credit' : 'invoice'}</span>
                            <span className="ml-2 text-slate-300">{r.name}</span>
                            <span className="ml-1 text-slate-600">#{r.code}</span>
                            {!r.customerLinked && <XCircle size={11} className="inline ml-1 text-amber-400" />}
                          </td>
                          <td className="px-3 py-1 border-b border-slate-800/40 text-right text-xs text-slate-500 font-mono whitespace-nowrap">
                            total {money(r.amount)}{r.cost ? <span className="text-slate-600"> · paid {money(r.cost)}</span> : null}
                          </td>
                          <td className="px-3 py-1 border-b border-slate-800/40 text-right text-xs font-mono text-slate-200">${money(r.balance)}</td>
                          <td className="px-3 py-1 border-b border-slate-800/40 text-[11px]">
                            {r.alreadySynced
                              ? <span className={r.modified ? 'text-amber-300' : 'text-green-400'}>{r.syncAction?.startsWith('Convert') ? (r.modified ? 'stale stub' : 'converted') : r.syncAction}{r.qboId ? ` → ${r.qboId}` : ''}</span>
                              : r.lastError ? <span className="text-red-300" title={r.lastError}>error: {r.lastError}</span> : null}
                            {r.warnings.length > 0 && <span className="text-amber-300 ml-1" title={r.warnings.join('\n')}>⚠ {r.warnings.join(' · ')}</span>}
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center gap-3 mt-3">
            <span className="text-xs text-slate-400">
              {selected.size} document(s) selected · open <span className="font-mono">${money(selectedOpen)}</span>
            </span>
            {blocked && <span className="text-xs text-red-300">Placeholder customer not found in QuickBooks — offsets cannot post.</span>}
            {running && job && (
              <div className="flex items-center gap-2 text-xs text-slate-300">
                <div className="w-40 h-2 rounded bg-slate-700 overflow-hidden"><div className="h-2 bg-blue-500 transition-all" style={{ width: `${job.total ? Math.round((100 * job.done) / job.total) : 0}%` }} /></div>
                <span className="font-mono">{job.done}/{job.total}</span>
                <button className="text-red-300 hover:underline" onClick={() => qboApi.cancelJob(job.jobId).catch(() => undefined)}>cancel</button>
              </div>
            )}
            <button
              className={`ml-auto px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors disabled:opacity-50 ${confirming ? 'bg-amber-600 hover:bg-amber-500' : 'bg-green-600 hover:bg-green-500'}`}
              onClick={run}
              disabled={running || selected.size === 0 || blocked || (offsetMode === 'fixed' && !offsetDate)}
            >
              {running ? 'Converting…' : confirming ? `Confirm: post ${selected.size} document(s) + offsets to QuickBooks` : `Convert selected (${selected.size})`}
            </button>
          </div>
        </div>
      )}

      <SyncResultList result={result} title="Conversion run" />
    </div>
  );
}
